import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain, CheckCircle, AlertCircle, Upload, Trash2, FileText, ChevronRight, ChevronLeft
} from '../../../components/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { isUserCoach } from '../../../lib/auth';
import { supabase } from '../../../lib/supabase';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import {
  fetchCoachConfig,
  saveCloneSetup,
  createVectorStore,
  uploadKnowledgeFile,
  listKnowledgeFiles,
  deleteKnowledgeFile,
  type CloneSetupData,
  type KnowledgeFile,
} from '../../../lib/ai-coach';

const STEPS = [
  { id: 'name', title: 'Ad ve Amaç', description: 'YZ Koçunuz nasıl anılacak?' },
  { id: 'style', title: 'Koçluk Tarzı', description: 'YZ Koçunuz nasıl iletişim kuracak?' },
  { id: 'principles', title: 'İlkeler', description: 'Temel koçluk metodolojiniz' },
  { id: 'boundaries', title: 'Sınırlar', description: 'YZ Koçunuzun kaçınması gerekenler' },
  { id: 'welcome', title: 'Karşılama', description: 'İlk izlenim önemlidir' },
  { id: 'knowledge', title: 'Bilgi Tabanı', description: 'Materyallerinizi yükleyin' },
  { id: 'access', title: 'Erişim ve Onay', description: 'YZ Koçunuzu kimler kullanabilir?' },
];

export function AICoachSetupWizardTR() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [formData, setFormData] = useState<CloneSetupData>({
    cloneName: '',
    cloneDescription: '',
    coachingStyle: '',
    idealClient: '',
    principles: '',
    dosAndDonts: '',
    disclaimers: '',
    welcomeMessage: 'Hoş geldiniz! Bugün size nasıl yardımcı olabilirim?',
    starterQuestions: ['', '', ''],
    clientAccessEnabled: false,
  });

  const [knowledgeFiles, setKnowledgeFiles] = useState<KnowledgeFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isCreatingStore, setIsCreatingStore] = useState(false);
  const [hasVectorStore, setHasVectorStore] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/tr/login'); return; }

    const init = async () => {
      try {
        const coach = await isUserCoach(user.id);
        if (!coach) { navigate('/tr/login'); return; }

        const { data: coachRecord } = await supabase
          .from('coaches').select('id').eq('user_id', user.id).maybeSingle();
        if (!coachRecord) { navigate('/tr/login'); return; }
        setCoachId(coachRecord.id);

        const config = await fetchCoachConfig(coachRecord.id);
        if (config) {
          setFormData({
            cloneName: config.cloneName,
            cloneDescription: config.cloneDescription,
            coachingStyle: config.coachingStyle,
            idealClient: config.idealClient,
            principles: config.principles,
            dosAndDonts: config.dosAndDonts,
            disclaimers: config.disclaimers,
            welcomeMessage: config.welcomeMessage || 'Hoş geldiniz! Bugün size nasıl yardımcı olabilirim?',
            starterQuestions: config.starterQuestions.length > 0 ? config.starterQuestions : ['', '', ''],
            clientAccessEnabled: config.clientAccessEnabled,
          });
          setHasVectorStore(!!config.vectorStoreId);
        }

        try {
          const files = await listKnowledgeFiles();
          setKnowledgeFiles(files);
        } catch (_) { /* henüz dosya yok */ }
      } catch (err) {
        console.error('Error loading setup:', err);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [user, authLoading, navigate]);

  const updateField = (field: keyof CloneSetupData, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateVectorStore = async () => {
    if (!coachId) return;
    setIsCreatingStore(true);
    try {
      await createVectorStore(coachId);
      setHasVectorStore(true);
      setToast({ type: 'success', message: 'Bilgi tabanı başarıyla oluşturuldu' });
    } catch (err: any) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setIsCreatingStore(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      await uploadKnowledgeFile(file);
      const files = await listKnowledgeFiles();
      setKnowledgeFiles(files);
      setToast({ type: 'success', message: `Yüklendi: ${file.name}` });
    } catch (err: any) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    try {
      await deleteKnowledgeFile(fileId);
      setKnowledgeFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (err: any) {
      setToast({ type: 'error', message: err.message });
    }
  };

  const handleSave = async () => {
    if (!coachId) return;
    setIsSaving(true);
    try {
      const cleanedData = {
        ...formData,
        starterQuestions: formData.starterQuestions.filter(q => q.trim() !== ''),
      };
      await saveCloneSetup(coachId, cleanedData);
      setToast({ type: 'success', message: 'YZ Koç Klonu başarıyla kaydedildi!' });
      setTimeout(() => navigate('/tr/coach/ai-settings'), 1500);
    } catch (err: any) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const canProceed = () => {
    if (currentStep === 0) return formData.cloneName.trim().length > 0;
    return true;
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Kurulum sihirbazı yükleniyor..." />;
  }

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-sage-500 rounded-xl shadow-soft">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="freud-heading-xl text-chocolate-900">YZ Koç Klonu Kurulumu</h1>
              <p className="freud-text-sm text-chocolate-500">
                Adım {currentStep + 1} / {STEPS.length}: {STEPS[currentStep].description}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Ilerleme cubugu */}
      <div className="max-w-4xl mx-auto px-4 pt-6">
        <div className="flex gap-1.5">
          {STEPS.map((step, idx) => (
            <button
              key={step.id}
              onClick={() => setCurrentStep(idx)}
              className={`flex-1 h-2 rounded-full transition-all ${
                idx <= currentStep ? 'bg-sage-500' : 'bg-cream-300'
              }`}
            />
          ))}
        </div>
        <div className="flex justify-between mt-2">
          {STEPS.map((step, idx) => (
            <span key={step.id} className={`text-[10px] font-medium ${
              idx === currentStep ? 'text-sage-700' : 'text-chocolate-400'
            }`}>
              {step.title}
            </span>
          ))}
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex items-center gap-2 p-4 rounded-xl mb-6 ${
              toast.type === 'success' ? 'bg-sage-100 text-sage-700' : 'bg-red-100 text-red-700'
            }`}
          >
            {toast.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <p className="text-sm font-medium">{toast.message}</p>
          </motion.div>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="freud-card-elevated p-8"
          >
            {/* Adım 0: Ad ve Amaç */}
            {currentStep === 0 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    YZ Koç Klonu Adı *
                  </label>
                  <input
                    type="text"
                    value={formData.cloneName}
                    onChange={(e) => updateField('cloneName', e.target.value)}
                    placeholder="örn. Koç Ayşe, Zihniyet Mentoru, Büyüme Rehberi"
                    className="freud-input text-lg"
                  />
                  <p className="text-xs text-chocolate-400 mt-1">YZ Koçunuz kendini bu isimle tanıtacak</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    YZ Koçunuz ne konuda yardımcı oluyor?
                  </label>
                  <textarea
                    value={formData.cloneDescription}
                    onChange={(e) => updateField('cloneDescription', e.target.value)}
                    placeholder="örn. Profesyonellerin zihniyet koçluğu, sorumluluk ve kişisel gelişim stratejileri aracılığıyla tam potansiyellerini ortaya çıkarmalarına yardımcı oluyorum."
                    rows={4}
                    className="freud-input resize-none"
                  />
                </div>
              </div>
            )}

            {/* Adım 1: Koçluk Tarzı */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Koçluk Tarzı ve Ses Tonu
                  </label>
                  <textarea
                    value={formData.coachingStyle}
                    onChange={(e) => updateField('coachingStyle', e.target.value)}
                    placeholder="örn. Sıcak ve cesaretlendirici ama doğrudan. İçgörüyü kısmak için sorular kullanır. Meydan okuma ile desteği dengeler. Sohbet dilinde konuşur, jargondan kaçınır."
                    rows={5}
                    className="freud-input resize-none"
                  />
                  <p className="text-xs text-chocolate-400 mt-1">YZ Koçunuzun nasıl iletişim kurması gerektiğini açıklayın</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    İdeal Müşteri Profili
                  </label>
                  <textarea
                    value={formData.idealClient}
                    onChange={(e) => updateField('idealClient', e.target.value)}
                    placeholder="örn. Kariyerlerini ilerletmek, liderlik becerilerini geliştirmek veya büyük yaşam geçişlerinde yol bulmak isteyen 30-50 yaş arası profesyoneller."
                    rows={3}
                    className="freud-input resize-none"
                  />
                </div>
              </div>
            )}

            {/* Adım 2: İlkeler */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Temel Koçluk İlkeleri ve Metodolojisi
                  </label>
                  <textarea
                    value={formData.principles}
                    onChange={(e) => updateField('principles', e.target.value)}
                    placeholder={"örn.\n- Sabit zihniyete karşı büyüme zihniyeti\n- Küçük tutarlı eylemler birikerek büyür\n- Öz farkındalık değişimin temelidir\n- Sınırlandırıcı inançlara kanıt göstererek meydan oku\n- Sonuçları değil, ilerlemeyi kutla"}
                    rows={8}
                    className="freud-input resize-none"
                  />
                  <p className="text-xs text-chocolate-400 mt-1">Koçluğunuza rehberlik eden temel inanç ve yöntemlerinizi listeleyin</p>
                </div>
              </div>
            )}

            {/* Adım 3: Sınırlar */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Yapılacaklar ve Yapılmayacaklar
                  </label>
                  <textarea
                    value={formData.dosAndDonts}
                    onChange={(e) => updateField('dosAndDonts', e.target.value)}
                    placeholder={"örn.\nYAPILACAKLAR: Güçlü sorular sor, başarıları kutla, uygulanabilir adımlar öner\nYAPILMAYACAKLAR: Tıbbi tavsiye verme, teşhis koyma, sonuçlar hakkında sözler verme, diğer müşterilerden bahsetme"}
                    rows={5}
                    className="freud-input resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Yasal Uyarılar ve Sınırlar
                  </label>
                  <textarea
                    value={formData.disclaimers}
                    onChange={(e) => updateField('disclaimers', e.target.value)}
                    placeholder="örn. Bu YZ Koçu yalnızca kişisel gelişim amaçlıdır. Profesyonel terapi, danışmanlık veya tıbbi tavsiyenin yerini tutmaz. Kriz durumundaysanız lütfen nitelikli bir uzmanla iletişime geçin."
                    rows={4}
                    className="freud-input resize-none"
                  />
                </div>
              </div>
            )}

            {/* Adım 4: Karşılama ve Başlangıç */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Karşılama Mesajı
                  </label>
                  <textarea
                    value={formData.welcomeMessage}
                    onChange={(e) => updateField('welcomeMessage', e.target.value)}
                    placeholder="örn. Merhaba! Ben kişisel YZ Koçunuzum. Potansiyelinizi keşfetmek ve hedeflerinize doğru çalışman için buradayım. Bugün ne üzerine odaklanmak istersiniz?"
                    rows={3}
                    className="freud-input resize-none"
                  />
                  <p className="text-xs text-chocolate-400 mt-1">Danışanların sohbeti açtıktan sonra gördüğü ilk mesaj</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-chocolate-800 mb-2">
                    Önerilen Başlangıç Soruları
                  </label>
                  <p className="text-xs text-chocolate-400 mb-3">Danışanlara sohbeti nasıl başlatacakları konusunda fikir verin</p>
                  {formData.starterQuestions.map((q, idx) => (
                    <input
                      key={idx}
                      type="text"
                      value={q}
                      onChange={(e) => {
                        const updated = [...formData.starterQuestions];
                        updated[idx] = e.target.value;
                        updateField('starterQuestions', updated);
                      }}
                      placeholder={`Başlangıç sorusu ${idx + 1}`}
                      className="freud-input mb-2"
                    />
                  ))}
                  {formData.starterQuestions.length < 6 && (
                    <button
                      onClick={() => updateField('starterQuestions', [...formData.starterQuestions, ''])}
                      className="text-sm text-sage-600 hover:text-sage-700 font-medium"
                    >
                      + Başka soru ekle
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Adım 5: Bilgi Tabanı */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <p className="text-sm text-chocolate-600">
                  YZ Koçunuzun referans alması için belgeler, çerçeveler veya eğitim materyalleri yükleyin.
                  Desteklenen formatlar: PDF, TXT, DOCX, MD.
                </p>

                {!hasVectorStore ? (
                  <div className="text-center py-8 border-2 border-dashed border-cream-300 rounded-xl">
                    <FileText className="w-10 h-10 text-cream-400 mx-auto mb-3" />
                    <p className="text-sm text-chocolate-500 mb-4">Dosya yüklemek için önce bilgi tabanını oluşturun</p>
                    <button
                      onClick={handleCreateVectorStore}
                      disabled={isCreatingStore}
                      className="freud-btn-primary"
                    >
                      {isCreatingStore ? 'Oluşturuluyor...' : 'Bilgi Tabanı Oluştur'}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="border-2 border-dashed border-cream-300 rounded-xl p-6 text-center">
                      <Upload className="w-8 h-8 text-cream-400 mx-auto mb-2" />
                      <label className="cursor-pointer">
                        <span className="text-sm text-sage-600 font-semibold hover:text-sage-700">
                          {isUploading ? 'Yükleniyor...' : 'Dosya yüklemek için tıklayın'}
                        </span>
                        <input
                          type="file"
                          onChange={handleFileUpload}
                          disabled={isUploading}
                          accept=".pdf,.txt,.docx,.md,.csv"
                          className="hidden"
                        />
                      </label>
                      <p className="text-xs text-chocolate-400 mt-1">Dosya başına maksimum 50MB</p>
                    </div>

                    {knowledgeFiles.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-chocolate-700">Yüklenen Dosyalar</h4>
                        {knowledgeFiles.map(file => (
                          <div key={file.id} className="flex items-center justify-between py-2.5 px-4 bg-cream-50 rounded-lg border border-cream-200">
                            <div className="flex items-center gap-3">
                              <FileText className="w-4 h-4 text-chocolate-400" />
                              <div>
                                <p className="text-sm font-medium text-chocolate-700">{file.filename}</p>
                                <p className="text-xs text-chocolate-400">
                                  {(file.file_size / 1024).toFixed(1)} KB
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleDeleteFile(file.id)}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Adım 6: Erişim ve Onay */}
            {currentStep === 6 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-cream-50 rounded-xl border border-cream-200">
                  <div>
                    <p className="text-sm font-semibold text-chocolate-800">Müşteri Erişimi</p>
                    <p className="text-xs text-chocolate-500">Danışanlarınızın/müşterilerinizin YZ Koçunuzla sohbet etmesine izin verin</p>
                  </div>
                  <button
                    onClick={() => updateField('clientAccessEnabled', !formData.clientAccessEnabled)}
                    className="group flex items-center"
                  >
                    <div className={`relative w-14 h-7 rounded-full transition-colors ${
                      formData.clientAccessEnabled ? 'bg-sage-500' : 'bg-gray-300'
                    }`}>
                      <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform ${
                        formData.clientAccessEnabled ? 'translate-x-[30px]' : 'translate-x-0.5'
                      }`} />
                    </div>
                  </button>
                </div>

                <div className="p-4 bg-sage-50 rounded-xl border border-sage-200">
                  <h4 className="text-sm font-semibold text-sage-800 mb-3">Kurulum Özeti</h4>
                  <div className="space-y-2 text-sm text-sage-700">
                    <p><span className="font-medium">Ad:</span> {formData.cloneName || '(belirlenmedi)'}</p>
                    <p><span className="font-medium">Amaç:</span> {formData.cloneDescription ? formData.cloneDescription.slice(0, 80) + '...' : '(belirlenmedi)'}</p>
                    <p><span className="font-medium">Tarzı:</span> {formData.coachingStyle ? 'Yapılandırıldı' : '(belirlenmedi)'}</p>
                    <p><span className="font-medium">İlkeler:</span> {formData.principles ? 'Yapılandırıldı' : '(belirlenmedi)'}</p>
                    <p><span className="font-medium">Sınırlar:</span> {formData.dosAndDonts || formData.disclaimers ? 'Yapılandırıldı' : '(belirlenmedi)'}</p>
                    <p><span className="font-medium">Bilgi dosyaları:</span> {knowledgeFiles.length} yüklendi</p>
                    <p><span className="font-medium">Müşteri erişimi:</span> {formData.clientAccessEnabled ? 'Açık' : 'Kapalı'}</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigasyon */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
            disabled={currentStep === 0}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-chocolate-600 font-semibold rounded-xl hover:bg-cream-100 transition-all disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4" />
            Geri
          </button>

          {currentStep < STEPS.length - 1 ? (
            <button
              onClick={() => setCurrentStep(prev => prev + 1)}
              disabled={!canProceed()}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-sage-600 text-white font-semibold rounded-xl hover:bg-sage-700 transition-all disabled:opacity-50"
            >
              İleri
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={isSaving || !formData.cloneName.trim()}
              className="inline-flex items-center gap-2 px-8 py-2.5 bg-sage-600 text-white font-semibold rounded-xl hover:bg-sage-700 transition-all disabled:opacity-50"
            >
              {isSaving ? 'Kaydediliyor...' : 'Kaydet ve Etkinleştir'}
              <CheckCircle className="w-4 h-4" />
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
